import { useMemo } from "react";
import { useNavigate } from "react-router";
import { X } from "lucide-react";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { useGraphShared, useSharedTracks } from "@web/features/library/api.ts";
import { formatDuration } from "@web/lib/format.ts";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { useBrowseTrailStore } from "@web/stores/browseTrail.ts";

/**
 * Bottom drawer of shared tracks/albums between two artists — local library only.
 */
export function ExploreDrawer() {
  const drawer = useBrowseTrailStore((s) => s.drawer);
  const closeDrawer = useBrowseTrailStore((s) => s.closeDrawer);
  const openDetail = useOpenDetail();
  const navigate = useNavigate();

  const legacyTracks = useSharedTracks(
    drawer?.kind === "shared_tracks" ? drawer.artistId : null,
    drawer?.kind === "shared_tracks" ? drawer.withArtistId : null,
  );
  const graphShared = useGraphShared(
    drawer?.kind === "graph_edge" ? drawer.artistId : null,
    drawer?.kind === "graph_edge" ? drawer.withArtistId : null,
  );

  const trackIdsForGrid = useMemo(() => {
    if (!drawer) return [] as string[];
    if (drawer.kind === "graph_edge") {
      return (graphShared.data?.tracks ?? []).map((t) => t.id);
    }
    return (legacyTracks.data ?? []).map((t) => t.id);
  }, [drawer, graphShared.data, legacyTracks.data]);

  if (!drawer) return null;

  const isGraph = drawer.kind === "graph_edge";
  const loading = isGraph ? graphShared.isLoading : legacyTracks.isLoading;
  const error = isGraph ? graphShared.isError : legacyTracks.isError;
  const errObj = isGraph ? graphShared.error : legacyTracks.error;
  const trackCount = isGraph
    ? (graphShared.data?.tracks.length ?? 0)
    : (legacyTracks.data?.length ?? 0);
  const albumCount = isGraph ? (graphShared.data?.albums.length ?? 0) : 0;

  function openEntity(type: "track" | "album" | "artist", id: string, label: string) {
    openDetail({ type, id, label }, { mode: "push" });
  }

  function openInGrid() {
    if (trackIdsForGrid.length === 0) return;
    const filter = JSON.stringify({
      field: "track.id",
      cmp: "in",
      value: trackIdsForGrid,
    });
    closeDrawer();
    void navigate(`/all?filter=${encodeURIComponent(filter)}`);
  }

  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-center p-2"
      role="presentation"
    >
      <div
        role="dialog"
        aria-label={`Items shared with ${drawer.withLabel}`}
        className="pointer-events-auto flex max-h-[40vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-line bg-panel-elevated shadow-lg"
      >
        <header className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-4 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">
              {drawer.artistLabel} · {drawer.withLabel}
            </p>
            <p className="text-xs text-faint">
              {loading
                ? "Loading…"
                : [
                    `${trackCount.toLocaleString()} track${trackCount === 1 ? "" : "s"}`,
                    albumCount > 0
                      ? `${albumCount.toLocaleString()} album${albumCount === 1 ? "" : "s"}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {trackCount > 0 && (
              <Button variant="ghost" size="sm" onClick={openInGrid}>
                Open in grid
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close shared items"
              onClick={closeDrawer}
            >
              <X className="size-4" />
            </Button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading && (
            <div className="space-y-2 p-3">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}
          {error && (
            <p className="p-4 text-sm text-status-cancelled">
              {errObj instanceof Error ? errObj.message : "Failed to load"}
            </p>
          )}

          {isGraph && graphShared.isSuccess && (
            <>
              {graphShared.data.albums.length > 0 && (
                <section className="border-b border-line">
                  <h3 className="px-3 pt-3 pb-1 text-xs font-semibold tracking-wide text-faint uppercase">
                    Albums
                  </h3>
                  <ul className="divide-y divide-line">
                    {graphShared.data.albums.map((album) => (
                      <li key={album.id}>
                        <button
                          type="button"
                          className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-row-hover"
                          onClick={() =>
                            openEntity("album", album.id, album.name ?? "Album")
                          }
                        >
                          <EntityAvatar src={album.cover_url} alt="" size="sm" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">
                              {album.name ?? "Album"}
                            </span>
                            <span className="block truncate text-xs text-faint">
                              {album.release_date ?? "Album-only link"}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              <section>
                {graphShared.data.tracks.length > 0 && (
                  <h3 className="px-3 pt-3 pb-1 text-xs font-semibold tracking-wide text-faint uppercase">
                    Tracks
                  </h3>
                )}
                {graphShared.data.tracks.length === 0 &&
                  graphShared.data.albums.length === 0 && (
                    <p className="p-4 text-sm text-faint">No shared items in this library.</p>
                  )}
                <ul className="divide-y divide-line">
                  {graphShared.data.tracks.map((track) => (
                    <li key={track.id}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-row-hover"
                        onClick={() => openEntity("track", track.id, track.name)}
                      >
                        <EntityAvatar src={track.cover_url} alt="" size="sm" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">
                            {track.name}
                          </span>
                          <span className="block truncate text-xs text-faint">
                            {track.artist_names.join(", ") || "—"}
                            {track.album_name ? ` · ${track.album_name}` : ""}
                          </span>
                        </span>
                        <span className="shrink-0 font-mono text-xs text-faint tabular-nums">
                          {formatDuration(track.duration_ms)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}

          {!isGraph && legacyTracks.isSuccess && legacyTracks.data.length === 0 && (
            <p className="p-4 text-sm text-faint">No shared tracks in this library.</p>
          )}
          {!isGraph && legacyTracks.isSuccess && legacyTracks.data.length > 0 && (
            <ul className="divide-y divide-line">
              {legacyTracks.data.map((track) => (
                <li key={track.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-row-hover"
                    onClick={() => openEntity("track", track.id, track.name)}
                  >
                    <EntityAvatar
                      src={track.cover_url ?? track.album?.cover_url}
                      alt=""
                      size="sm"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{track.name}</span>
                      <span className="block truncate text-xs text-faint">
                        {track.artists.map((a) => a.name).filter(Boolean).join(", ") || "—"}
                      </span>
                    </span>
                    <span className="shrink-0 font-mono text-xs text-faint tabular-nums">
                      {formatDuration(track.duration_ms)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
